import { useState, useEffect } from "react";
import { deleteService, startService, completeService, fetchValidation } from "@/lib/api";
import { Pencil, Trash2, Play, CheckCircle2, Circle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SearchableCombobox from "@/components/SearchableCombobox";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";

const fmtMoney = (n) => (n === null || n === undefined || n === "" ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n));

// Small visual chip for status so the table telegraphs state at a glance.
export function StatusChip({ status }) {
  const s = status || "Logged";
  const map = {
    "Logged": { bg: "bg-[#2B2214]/[0.12]", fg: "text-[#3A2A10]", icon: Circle },
    "In Progress": { bg: "bg-[#D4AF37]/20", fg: "text-[#0A0604]", icon: Play },
    "Complete": { bg: "bg-emerald-700/15", fg: "text-emerald-800", icon: CheckCircle2 },
  };
  // Any custom user-defined status falls into a neutral parchment chip so
  // the value is still visible but doesn't fight the three system statuses.
  const cfg = map[s] || { bg: "bg-[#8B6A1F]/15", fg: "text-[#3A2A10]", icon: Circle };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm ${cfg.bg} ${cfg.fg}`}>
      <Icon size={10} /> {s}
    </span>
  );
}

function ServiceActionButtons({ s, onStart, onComplete, onEdit, onDelete }) {
  const isLogged = (s.status || "Logged") === "Logged";
  const isInProgress = s.status === "In Progress";
  return (
    <div className="flex items-center justify-end gap-1">
      {isLogged && (
        <button
          onClick={() => onStart(s)}
          className="px-2 py-1 text-[10px] uppercase tracking-wider text-[#0A0604] bg-[#D4AF37] hover:bg-[#E5C158] rounded-sm"
          data-testid={`service-start-${s.id}`}
          title="Start service (flips watch to Being Serviced)"
        >
          <Play size={10} className="inline mr-1" /> Start
        </button>
      )}
      {isInProgress && (
        <button
          onClick={() => onComplete(s)}
          className="px-2 py-1 text-[10px] uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm"
          data-testid={`service-complete-${s.id}`}
          title="Mark complete and update watch status"
        >
          <CheckCircle2 size={10} className="inline mr-1" /> Complete
        </button>
      )}
      <button onClick={() => onEdit(s)} className="p-1.5 text-[#3A2A10] hover:text-[#0A0604]" data-testid={`service-edit-${s.id}`} aria-label="Edit"><Pencil size={13} /></button>
      <button onClick={() => onDelete(s)} className="p-1.5 text-[#3A2A10] hover:text-rose-500" data-testid={`service-delete-${s.id}`} aria-label="Delete"><Trash2 size={13} /></button>
    </div>
  );
}

export default function ServicesTable({ services, onEdit, onReload }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const [completing, setCompleting] = useState(null);     // service being completed
  const [completeStatus, setCompleteStatus] = useState(""); // chosen watch status
  const [completeNotes, setCompleteNotes] = useState("");
  const [validation, setValidation] = useState({});

  useEffect(() => {
    fetchValidation().then(setValidation).catch(() => {});
  }, []);

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await deleteService(confirmDel.id);
      toast.success("Service entry removed");
      setConfirmDel(null);
      onReload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const doStart = async (s) => {
    try {
      await startService(s.id);
      toast.success("Service started — watch flipped to Being Serviced");
      onReload();
    } catch (err) {
      toast.error(errorMessage(err, "Start failed"));
    }
  };

  const doComplete = async () => {
    if (!completing) return;
    try {
      await completeService(completing.id, {
        watch_status: completeStatus || null,
        completion_notes: completeNotes || null,
      });
      toast.success(completeStatus
        ? `Service complete — watch is now ${completeStatus}`
        : "Service marked complete");
      setCompleting(null);
      setCompleteStatus("");
      setCompleteNotes("");
      onReload();
    } catch (err) {
      toast.error(errorMessage(err, "Complete failed"));
    }
  };

  if (services.length === 0) {
    return (
      <div className="border border-[#8B6A1F]/35 bg-[#F9F0D4] py-16 text-center" data-testid="services-empty">
        <div className="font-serif-display text-2xl text-[#0A0604] mb-2">No services logged</div>
        <div className="text-xs text-[#3A2A10] uppercase tracking-[0.2em]">Log an overhaul, cleaning, or regulation to begin</div>
      </div>
    );
  }

  // Action buttons live above (as `ServiceActionButtons`). Pass callbacks
  // down so the nested component isn't recreated on every render.
  const renderActions = (s) => (
    <ServiceActionButtons
      s={s}
      onStart={doStart}
      onComplete={setCompleting}
      onEdit={onEdit}
      onDelete={setConfirmDel}
    />
  );

  return (
    <div data-testid="services-table">
      {/* Card layout at every viewport width — the 7-column table was
          impossible to make readable across sidebars, browser zoom levels,
          and tablet widths, and Notes always won the "who gets squeezed"
          fight. Cards stack cleanly and let notes/description breathe. */}
      <div className="space-y-3">
        {services.map((s) => (
          <div
            key={s.id}
            className="bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm p-4"
            data-testid={`service-card-${s.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <StatusChip status={s.status} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-mono-data">
                  {s.service_date || "—"}
                </span>
                {s.cost ? (
                  <span className="text-[11px] font-mono-data text-[#3A2A10]">
                    {fmtMoney(s.cost)}
                  </span>
                ) : null}
              </div>
              {renderActions(s)}
            </div>
            <div className="flex items-baseline gap-3 flex-wrap mb-1">
              <div className="font-serif-display text-xl text-[#0A0604] leading-tight">
                {s.service_type || "Service"}
              </div>
              {s.performed_by && (
                <div className="text-[11px] text-[#3A2A10]">by {s.performed_by}</div>
              )}
            </div>
            {s.description && (
              <div
                className="text-sm text-[#0A0604] whitespace-pre-wrap break-words mt-1"
                data-testid={`service-description-${s.id}`}
              >
                {s.description}
              </div>
            )}
            {s.notes && (
              <div
                className="mt-2 pl-3 border-l-2 border-[#D4AF37]/50 text-sm text-[#3A2A10] whitespace-pre-wrap break-words"
                data-testid={`service-notes-${s.id}`}
              >
                {s.notes}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-2xl">Remove service entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#3A2A10]">This will permanently delete the service record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#8B6A1F]/45 rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm" data-testid="service-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Complete modal — prompts for new watch status */}
      <Dialog open={!!completing} onOpenChange={(o) => { if (!o) { setCompleting(null); setCompleteStatus(""); setCompleteNotes(""); } }}>
        <DialogContent className="bg-[#FBF3D9] border-[#8B6A1F]/45 rounded-sm max-w-md" data-testid="service-complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#0A0604]">Complete service</DialogTitle>
            <DialogDescription className="text-[#3A2A10]">
              Marking this service complete. What&apos;s the watch&apos;s new status?
              Leave blank to keep it where it is.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">New watch status</div>
              <SearchableCombobox
                value={completeStatus}
                onChange={setCompleteStatus}
                options={validation["Status"] || []}
                testid="service-complete-watch-status"
                placeholder="No change"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["Overhauled", "For Sale", "Running", "Owned"].map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setCompleteStatus(q)}
                    className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${completeStatus === q ? "bg-[#D4AF37] text-[#0A0604] border-[#D4AF37]" : "border-[#8B6A1F]/35 text-[#3A2A10] hover:bg-[#2B2214]/[0.06]"}`}
                    data-testid={`service-complete-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">Completion notes (optional)</div>
              <textarea
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                rows={3}
                placeholder="What was done, any amplitude / rate numbers, parts used…"
                className="w-full px-3 py-2 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37] resize-none"
                data-testid="service-complete-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setCompleting(null); setCompleteStatus(""); setCompleteNotes(""); }}
              className="px-4 h-10 border border-[#8B6A1F]/45 rounded-sm text-[#3A2A10] hover:bg-[#2B2214]/[0.06] text-xs uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              onClick={doComplete}
              className="px-4 h-10 bg-emerald-700 hover:bg-emerald-800 text-white text-xs uppercase tracking-wider rounded-sm"
              data-testid="service-complete-confirm"
            >
              Mark Complete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
